import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { addDays, format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useFormik } from 'formik';

import FormFooter from '../../../containers/form-footer';
import IconBtn from '../../../modules/icon-btn/icon-btn';
import ModuleInfo from '../../../modules/module-info/module-info';
import ModuleTitleText from '../../../modules/module-title-text/module-title-text';
import Step from '../../../modules/step';
import UiInput from '../../../modules/ui/ui-input/ui-input';
import UiSelect, { OptionType } from '../../../modules/ui/ui-select/ui-select';
import { CreditConstraintsResponse } from '../../../services/api/dto/Credits';
import { useAppDispatch } from '../../../store';
import {
  initCredit,
  setCreditDetails,
} from '../../../store/actions/operations-sign';
import currencyFormat from '../../../utils/currencyFormat';
import getWordDecl from '../../../utils/getWordDecl';
import BuyIcon from '../cfa-release-form/icons/buy-icon.svg';

import './assets-credit-form.scss';

interface AssetsCreditFormProps {
  constraints?: CreditConstraintsResponse;
  loadingConstraints?: boolean;
  value?: number;
  period?: number;
}

type FormStateType = {
  amount?: string;
  term?: string;
};

const initialFormState: FormStateType = {};

const TermValues = [
  {
    value: '30',
    label: '30 дней',
  },
  {
    value: '45',
    label: '45 дней',
  },
  {
    value: '60',
    label: '60 дней',
  },
  {
    value: '90',
    label: '90 дней',
  },
];

const AssetsCreditForm: React.FC<AssetsCreditFormProps> = ({
  constraints,
  loadingConstraints,
  value,
  period,
}) => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [loadingBtn, setLoadingBtn] = useState(false);
  let getCreditFormulaData: { totalSum: number; creditSum: number } | undefined;

  const formState = useFormik({
    initialValues: initialFormState,
    onSubmit: (values) => {
      if (getCreditFormulaData && values.amount && values.term) {
        setLoadingBtn(true);
        const { totalSum, creditSum } = getCreditFormulaData;

        dispatch(
          setCreditDetails({
            fromDate: format(new Date(), 'dd MMM yyyy HH:mm', { locale: ru }),
            toDate: format(
              addDays(new Date(), parseInt(values.term, 10)),
              'dd MMM yyyy HH:mm',
              { locale: ru }
            ),
            rate: constraints?.interestRate || 0,
            sum: totalSum,
            creditSum,
            creditorName: 'ООО “Лайтхаус”',
          })
        );
        dispatch(
          initCredit({
            term: parseInt(values.term, 10),
            amount: parseFloat(values.amount.replace(/\s/g, '')),
          })
        ).then((action) => {
          if (action.meta.requestStatus === 'fulfilled') {
            history.push('/credit/sign');
          }
        });
      }
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (constraints) {
        if (
          values.amount &&
          parseFloat(values.amount.replace(/\s/g, '')) < constraints.limit.min
        ) {
          errors.amount = `Минимальная сумма ${currencyFormat(
            constraints.limit.min
          )}`;
        }
        if (
          values.amount &&
          parseFloat(values.amount.replace(/\s/g, '')) > constraints.limit.max
        ) {
          errors.amount = `Максимальная сумма ${currencyFormat(
            constraints.limit.max
          )}`;
        }
        if (
          values.term &&
          parseInt(values.term, 10) < constraints.term.min * 30
        ) {
          errors.term = `Минимальный срок ${
            constraints.term.min
          } ${getWordDecl(constraints.term.min, [
            'месяц',
            'месяца',
            'месяцeв',
          ])}`;
        }
        if (
          values.term &&
          parseInt(values.term, 10) > constraints.term.max * 30
        ) {
          errors.term = `Максимальный срок ${
            constraints.term.max
          } ${getWordDecl(constraints.term.max, [
            'месяц',
            'месяца',
            'месяцeв',
          ])}`;
        }
      }

      return errors;
    },
  });

  useEffect(() => {
    if (value) formState.setFieldValue('amount', String(value));
    if (period) formState.setFieldValue('term', String(period));

    if (value && period) formState.handleSubmit();
  }, [value, period]);

  getCreditFormulaData = useMemo(() => {
    if (constraints && formState.values.amount && formState.values.term) {
      const floatAmount = parseFloat(
        formState.values.amount.replace(/\s/g, '')
      );
      const floatTerm = parseInt(formState.values.term, 10);
      let delayPeriod = constraints.gracePeriod - floatTerm;
      if (delayPeriod < 0) delayPeriod *= -1;

      const dayPercent = constraints.interestRate / (365 * 100);
      const totalSum = floatAmount + delayPeriod * dayPercent * floatAmount;

      return { totalSum, creditSum: totalSum - floatAmount };
    }

    return undefined;
  }, [constraints, formState.values.amount, formState.values.term]);

  const getFieldError = (field: 'amount' | 'term') =>
    formState.errors[field] ? (
      <div className="form-item__footer">
        <span className="red">{formState.errors[field]}</span>
      </div>
    ) : null;

  return (
    <form className="form-assets">
      <div className="form-assets__item form-assets__item--sm">
        <Step
          steps={[
            { title: 'Выбор суммы', active: true },
            { title: 'Заявка' },
            { title: 'Статус заявки' },
          ]}
        />
      </div>
      <div className="form-assets__item form-assets__item--sm">
        <p className="text">Ознакомьтесь с параметрами займа:</p>
      </div>
      <div className="form-assets__item form-assets__item--sm">
        <div className="form-assets__module-info-list">
          <ModuleInfo
            topPrefix={constraints?.interestRate || ''}
            topPostfix="%"
            bottomDesc="ставка:"
            loading={loadingConstraints}
            widthSkeleton={72}
            heightSkeleton={73}
          />
          <ModuleInfo
            topPrefix={`${constraints?.term.min} - ${constraints?.term.max}`}
            topPostfix="мес"
            bottomDesc="срок:"
            loading={loadingConstraints}
            widthSkeleton={128}
            heightSkeleton={73}
          />
          <ModuleInfo
            topPrefix={constraints?.gracePeriod || ''}
            topPostfix="дней"
            bottomDesc="льготный период"
            loading={loadingConstraints}
            widthSkeleton={128}
            heightSkeleton={73}
          />
          <ModuleInfo
            topPrefix={constraints?.limit.max.toLocaleString('ru') || ''}
            topPostfix="₽"
            bottomDesc="доступный лимит"
            loading={loadingConstraints}
            widthSkeleton={224}
            heightSkeleton={73}
          />
        </div>
      </div>
      <div className="form-assets__item form-assets__item--sm">
        <p className="text">Заполните формы для расчета и получения</p>
      </div>
      <div className="form-assets__flex form-assets__flex--r24">
        <div className="form-assets__item form-assets__item--xs form-assets__item-176">
          <div className="form-item">
            <UiInput
              error={formState.errors.amount}
              onChange={(e: React.FormEvent<HTMLInputElement>) => {
                !Number.isNaN(
                  Number(e.currentTarget.value.replace(/\s/g, ''))
                ) && formState.setFieldValue('amount', e.currentTarget.value);
              }}
              value={
                formState.values.amount
                  ? Number(
                      formState.values.amount.replace(/\s/g, '')
                    ).toLocaleString('ru')
                  : undefined
              }
              name="amount"
              label="Сумма займа:"
              rightTextClassName="input-credit__rub-icon"
              rightText="₽"
              lg
            />
            {getFieldError('amount')}
          </div>
        </div>
        <div className="form-assets__item form-assets__item--xs form-assets__item-176">
          <div className="form-item">
            <UiSelect
              options={TermValues}
              onChange={(val) =>
                formState.setFieldValue(
                  'term',
                  Number((val as OptionType)?.value)
                )
              }
              value={TermValues.find(
                (item) => item.value === String(formState.values.term)
              )}
              label="Срок:"
              lg
            />
            {getFieldError('term')}
          </div>
        </div>
      </div>
      <FormFooter>
        <div className="form-assets__container">
          {getCreditFormulaData && (
            <div className="form-assets__flex">
              <div className="form-assets__item form-assets__item--sm">
                <ModuleTitleText
                  top="Общая сумма займа:"
                  bottom={currencyFormat(getCreditFormulaData.totalSum)}
                />
              </div>
              <div className="form-assets__item form-assets__item--sm">
                <ModuleTitleText
                  top="Стоимость займа:"
                  bottom={currencyFormat(getCreditFormulaData.creditSum)}
                />
              </div>
            </div>
          )}
          <div className="form-assets__item">
            <div className="form-item">
              <IconBtn
                disabled={
                  loadingBtn ||
                  !formState.isValid ||
                  !formState.values.amount ||
                  !formState.values.term
                }
                onClick={formState.handleSubmit}
                icon={BuyIcon}
                title="Оформить займ"
              />
            </div>
          </div>
        </div>
      </FormFooter>
    </form>
  );
};

export default AssetsCreditForm;
